import { useMemo } from 'react'

import { useItemsCalendario } from '@/core/hooks/useCalendario'
import { useAhora } from '@/hooks/useAhora'
import { usePermissions } from '@/hooks/usePermissions'
import { clasificarProximos, esMeetDelUsuario, FILTROS_SOLO_MEETS } from '@/lib/meets'

import type { ItemCalendario } from '@/core/types/calendario'
import type { MeetClasificado } from '@/lib/meets'

/** El item del calendario ya trae resuelta la fecha de la ocurrencia en `fecha` (ORR-383). */
type MeetItem = ItemCalendario & { fecha_inicio: string }

/**
 * Las reuniones desde hoy hasta `diasHorizonte`, clasificadas por franja.
 *
 * **Consulta dos meses a propósito.** Cualquier horizonte, aunque sea de un día, cruza el borde
 * del mes cuando hoy es el último: pidiendo solo el mes actual, las reuniones de mañana
 * desaparecen los días 30 y 31. El fetch vive acá y no en cada vista porque la primera versión lo
 * duplicaba y una de las dos copias se olvidó el segundo mes.
 *
 * Devuelve solo las reuniones que le corresponden al usuario, según `esMeetDelUsuario`.
 */
export function useMeetsProximos(diasHorizonte: number): {
  meets: MeetClasificado<MeetItem>[]
  ahora: Date
} {
  const ahora = useAhora()
  const { emailUsuario, esOwner } = usePermissions()

  const mesActual = useItemsCalendario(ahora.getFullYear(), ahora.getMonth() + 1, FILTROS_SOLO_MEETS)
  const siguiente = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 1)
  const mesSiguiente = useItemsCalendario(
    siguiente.getFullYear(),
    siguiente.getMonth() + 1,
    FILTROS_SOLO_MEETS,
  )

  const meets = useMemo(() => {
    const usuario = { email: emailUsuario, esOwner }
    const items: ItemCalendario[] = [...(mesActual.data ?? []), ...(mesSiguiente.data ?? [])]
    return clasificarProximos<MeetItem>(
      items
        .filter((item) => esMeetDelUsuario(item, usuario))
        .map((item) => ({ ...item, fecha_inicio: item.fecha })),
      ahora,
      diasHorizonte,
    )
  }, [mesActual.data, mesSiguiente.data, ahora, diasHorizonte, emailUsuario, esOwner])

  return { meets, ahora }
}
